import { type BasketItem } from '@koala/sdk';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type CSSProperties } from 'react';
import { ConnectedCartIcon } from '../account/uielements';
import { styled } from '@/components/content/stitches';
import { CSS_CLASSES } from '@/constants/cssClassNames';
import { ROUTES } from '@/constants/routes';
import { useDispatch, useSelector } from '@/redux';
import { useConfigOverride } from '@/redux/cmsConfig';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import globalActions from '@/redux/global/actions';
import meActions from '@/redux/me/actions';
import { getBasketItemsCountWithQuantity } from '@/utils/basket';
import { getLocationId } from '@/utils/locations';

const getBasketButtonContents = (
  basketItems: BasketItem[],
  badgeCss: {
    backgroundColor: string;
    color: string;
    boxShadow: string;
  },
) => {
  const count = getBasketItemsCountWithQuantity(basketItems);
  const displayCount = count > 9 ? '9+' : count;

  return (
    <IconWrapper>
      <ConnectedCartIcon />

      {Boolean(count) && <Badge css={badgeCss}>{displayCount}</Badge>}
    </IconWrapper>
  );
};

interface BasketProps {
  //showOnMobile is a flag for just showing the Basket IF mobile size
  showOnMobile: boolean;
}

export function Basket(props: BasketProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { webConfig } = useSelector((state) => state.app.cmsConfig);
  const { content, location } = useSelector((state) => state.app.basket);

  const { active_background_color, border_radius, active_font_color, active_border_color, font } =
    useConfigOverride('header');
  const { primary_active_color } = useConfigOverride('global');

  const overrides: CSSProperties = {
    backgroundColor: active_background_color ?? 'inherit',
    borderRadius: `${border_radius ?? 0}px`,
    color: active_font_color ?? 'inherit',
    borderColor: active_border_color ?? 'inherit',
    margin: '2px',
    ...getFontStyles(font, ['font_family', 'font_size', 'font_weight', 'line_height']),
  };

  //TODO: There is probably a better way to do this, inlining allowed me access to the props, which meant I could dynamically
  //change the elements display. So if showOnMobile, display on all screens but desktop.
  //Originally tried a simple .mobile_btn class in global.css, but some post-process css stuff happens in this app where
  //display gets modified out of scope of header.tsx and basket.tsx, this became the only solution given the time
  const Element = styled('button', {
    alignItems: 'center',
    border: '1px solid black',
    cursor: 'pointer',
    //By default, display the button, then at different break points do the opposite.
    //app changes header design after @md breakpoint, so showOnMobile will change its design
    display: props.showOnMobile ? 'flex' : 'none',
    height: '$9',
    justifyContent: 'center',
    minWidth: '$9',
    padding: '0 $3',

    '@md': {
      display: !props.showOnMobile ? 'flex' : 'none',
    },

    'img, svg': {
      height: '$4',
    },
  });

  const ringColor = active_font_color ?? 'white';
  const badgeOverrides = {
    backgroundColor: primary_active_color ?? active_font_color ?? 'inherit',
    color: ringColor,
    boxShadow: `0 0 0 2px ${ringColor}`,
  };

  const count = getBasketItemsCountWithQuantity(content.basket_items);
  const ariaLabel =
    count === 0
      ? 'Shopping cart'
      : count === 1
        ? 'Shopping cart, 1 item'
        : `Shopping cart, ${count} items`;

  if (router?.pathname === ROUTES.STORE || Object.keys(location).length === 0) {
    return (
      <Element
        data-testid="basket-button"
        aria-label={ariaLabel}
        onClick={() => {
          dispatch(meActions.toggleAccountDropdown(false));
          dispatch(globalActions.toggleBasket(true));
        }}
        className={CSS_CLASSES.GLOBAL_HEADER.ORDER_NOW}
        style={overrides}
      >
        {getBasketButtonContents(content.basket_items, badgeOverrides)}
      </Element>
    );
  }

  return (
    <BasketLink
      href={`/store/${getLocationId(location, webConfig)}/${location.label}?openCart=true`}
      data-testid="basket-button"
      aria-label={ariaLabel}
      className={CSS_CLASSES.GLOBAL_HEADER.ORDER_NOW}
      style={overrides}
      css={{
        display: props.showOnMobile ? 'flex' : 'none',
        '@md': {
          display: !props.showOnMobile ? 'flex' : 'none',
        },
      }}
    >
      {getBasketButtonContents(content.basket_items, badgeOverrides)}
    </BasketLink>
  );
}

const BasketLink = styled(Link, {
  alignItems: 'center',
  border: '1px solid black',
  cursor: 'pointer',
  height: '$9',
  justifyContent: 'center',
  minWidth: '$9',
  padding: '0 $3',
  textDecoration: 'none',
  color: 'inherit',

  'img, svg': {
    height: '$4',
  },
});

const IconWrapper = styled('span', {
  display: 'inline-flex',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
});

const Badge = styled('span', {
  alignItems: 'center',
  borderRadius: '999px',
  display: 'flex',
  fontSize: '11px',
  fontWeight: 'bold',
  justifyContent: 'center',
  lineHeight: '14px',
  minWidth: '20px',
  height: '20px',
  padding: '0 6px',
  position: 'absolute',
  top: '-16px',
  right: '-27px',

  '@md': {
    top: '-18px',
    right: '-18px',
  },
});
